export const PLATFORMS = ["instagram", "facebook", "tiktok"] as const;
export type Platform = (typeof PLATFORMS)[number];

export const CONTENT_KINDS = [
  "caption",
  "carousel",
  "script",
  "image_brief",
  "hashtags",
  "hook_options",
] as const;
export type ContentKind = (typeof CONTENT_KINDS)[number];

/**
 * The category of brand this is. The brain schema is the same for all kinds,
 * but defaults, content categories, and prompt assembly differ. Adding a new
 * kind is meant to be cheap.
 */
export const BRAND_KINDS = [
  "ecommerce", // physical or digital products with a catalog
  "service", // consultant, agency, photographer — sells services, no SKUs
  "creator", // personal brand, podcaster, thought leader
  "media", // publication, newsletter, content site
] as const;
export type BrandKind = (typeof BRAND_KINDS)[number];

/** Where a brand's product catalog comes from (only relevant when kind=ecommerce). */
export const PRODUCT_SOURCES = ["shopify", "woocommerce", "website", "manual", "none"] as const;
export type ProductSourceKind = (typeof PRODUCT_SOURCES)[number];

/** What an uploaded media asset depicts, so generation knows how to use it. */
export const MEDIA_PURPOSES = ["product", "logo", "lifestyle", "other"] as const;
export type MediaPurpose = (typeof MEDIA_PURPOSES)[number];

/**
 * Trend-scan sources a brand can pull trending topics from. reddit + youtube
 * read official APIs; googletrends reads Google's published daily
 * trending-searches RSS feed; instagram reads the official Instagram Graph
 * API Hashtag Search (requires a connected business account — adapter
 * pending); custom reads any RSS/Atom or JSON feed URL. Adding a source =
 * one arm of the discriminated union below + one adapter (see
 * packages/core/src/trends/).
 */
export const TREND_SOURCES = ["reddit", "youtube", "googletrends", "instagram", "custom"] as const;
export type TrendSourceKind = (typeof TREND_SOURCES)[number];

/** Whether a trend is gaining, at peak, or losing steam. */
export type TrendMomentum = "rising" | "peaking" | "fading";

/** Per-source configuration — the inputs each source needs to scan. */
export interface RedditSourceConfig {
  kind: "reddit";
  /** Subreddits to scan, without the "r/" prefix. */
  subreddits: string[];
}
export interface YouTubeSourceConfig {
  kind: "youtube";
  channelIds?: string[];
  keywords?: string[];
}
export interface InstagramSourceConfig {
  kind: "instagram";
  handles?: string[];
  hashtags?: string[];
}
export interface GoogleTrendsSourceConfig {
  kind: "googletrends";
  /** Two-letter region code for the daily-trends feed, e.g. "US", "GB". */
  geo: string;
}
export interface CustomSourceConfig {
  kind: "custom";
  /** RSS/Atom or JSON feed URL (SSRF-guarded at fetch time). */
  feedUrl: string;
}
export type TrendSourceConfig =
  | RedditSourceConfig
  | YouTubeSourceConfig
  | GoogleTrendsSourceConfig
  | InstagramSourceConfig
  | CustomSourceConfig;

export const MEMBER_ROLES = ["owner", "admin", "editor", "approver", "viewer"] as const;
export type MemberRole = (typeof MEMBER_ROLES)[number];

export interface ContentInput {
  id?: string;
  text: string;
  sourceUrl?: string;
  productHandles?: string[];
  mediaIds?: string[];
  notes?: string;
}

export type MediaAspect = "1:1" | "4:5" | "9:16" | "16:9";

/**
 * How a product appears in a generated image/video:
 *  - "composite": keep the real product pixels, generate the scene around them.
 *  - "reference": condition on the product image; the model redraws it.
 *  - "scene": pure text-to-image, no product on screen (lifestyle/abstract).
 */
export type ShotMode = "composite" | "reference" | "scene";

/** Whether a media asset was uploaded by a user or AI-generated. */
export type MediaOrigin = "uploaded" | "generated";

/**
 * Provenance for an AI-generated media asset — recorded so the library can
 * show how a shot was made, attribute its cost, and re-generate it.
 */
export interface MediaGeneration {
  provider: "bedrock" | "vertex";
  /** Registry model id, e.g. "stable-image". */
  modelId: string;
  capability: "image" | "video";
  prompt: string;
  shotMode: ShotMode;
  /** Products featured in the shot. */
  sourceProductHandles?: string[];
  /** Uploaded media used as references/inputs. */
  sourceMediaIds?: string[];
  /** Usage units this generation consumed (plan metering). */
  usageUnits: number;
  generatedBy: string;
  generatedAt: string;
}

/**
 * The visual spec for a draft — what the post will look like. Carried so
 * the UI can render the platform-appropriate placeholder (IG carousel
 * slides, TikTok video frame, FB/IG image) with shot briefs, per the
 * "visual honesty" design rule. Derived from the model's structured
 * output (carousel slides, script beats, image briefs).
 */
export interface DraftMedia {
  type: "image" | "carousel" | "video";
  aspect?: MediaAspect;
  /** Carousel slides — one brief per slide. */
  slides?: Array<{ description: string; kind?: string }>;
  /** Video cover-frame brief. */
  cover?: string;
  /** Video duration label, e.g. "0:14". */
  duration?: string;
  /** Caption preview strip on video. */
  caption?: string;
  /** Image shot brief. */
  description?: string;
  /**
   * Real generated/selected asset references (MediaRecord `sha` ids) attached
   * to this post. Populated once the user generates or picks actual media for
   * the variant; the text briefs above remain as the visual-honesty fallback
   * when no asset has been attached yet. Consumed by the publish worker.
   */
  mediaIds?: string[];
}

export interface DraftVariant {
  platform: Platform;
  kind: ContentKind;
  body: string;
  hashtags?: string[];
  hook?: string;
  imageBrief?: string;
  callToAction?: string;
  notes?: string;
  /** Visual spec for the post (set for content-bearing kinds). */
  media?: DraftMedia;
}

export interface Draft {
  id: string;
  createdAt: string;
  workspaceId: string;
  brandId: string;
  inputId?: string;
  variants: DraftVariant[];
  warnings: ValidationWarning[];
}

export interface ValidationWarning {
  variantIndex: number;
  rule: string;
  level: "warn" | "block";
  detail: string;
}
